import { Component, Inject, LOCALE_ID, Renderer2 } from '@angular/core';
import { ConfigService } from '../@vex/services/config.service';
import { Settings } from 'luxon';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { NavigationService } from '../@vex/services/navigation.service';
import icLayers from '@iconify/icons-ic/twotone-layers';
import icHome from '@iconify/icons-ic/twotone-home';
import icJobs from '@iconify/icons-ic/twotone-card-travel';
import icSearch from '@iconify/icons-ic/twotone-search';

import icAssigment from '@iconify/icons-ic/twotone-assignment';
import icContactSupport from '@iconify/icons-ic/twotone-contact-support';
import icDateRange from '@iconify/icons-ic/twotone-date-range';
import icChat from '@iconify/icons-ic/twotone-chat';
import icContacts from '@iconify/icons-ic/twotone-contacts';
import icGroups from '@iconify/icons-ic/baseline-groups';

import icAssessment from '@iconify/icons-ic/twotone-assessment';
import icLock from '@iconify/icons-ic/twotone-lock';
import icWatchLater from '@iconify/icons-ic/twotone-watch-later';
import icError from '@iconify/icons-ic/twotone-error';
import icAttachMoney from '@iconify/icons-ic/twotone-attach-money';
import icPersonOutline from '@iconify/icons-ic/twotone-person-outline';
import icReceipt from '@iconify/icons-ic/twotone-receipt';
import icHelp from '@iconify/icons-ic/twotone-help';
import icBook from '@iconify/icons-ic/twotone-book';
import icBubbleChart from '@iconify/icons-ic/twotone-bubble-chart';
import icFormatColorText from '@iconify/icons-ic/twotone-format-color-text';
import icStar from '@iconify/icons-ic/twotone-star';
import icViewCompact from '@iconify/icons-ic/twotone-view-compact';
import icPictureInPicture from '@iconify/icons-ic/twotone-picture-in-picture';
import icSettings from '@iconify/icons-ic/twotone-settings';
import { LayoutService } from '../@vex/services/layout.service';
import icUpdate from '@iconify/icons-ic/twotone-update';
import { ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { SplashScreenService } from '../@vex/services/splash-screen.service';
import { Style, StyleService } from '../@vex/services/style.service';
import icChromeReaderMode from '@iconify/icons-ic/twotone-chrome-reader-mode';
import { ConfigName } from '../@vex/interfaces/config-name.model';
import icMail from '@iconify/icons-ic/twotone-mail';
import icHelpOutline from '@iconify/icons-ic/twotone-help-outline';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/@vex/services/authenticationService/authentication.service';
import { LanguageTranslationService } from 'src/@vex/services/languageTranslationService/language-translation.service';
import { Title } from '@angular/platform-browser';
import { titleConfigs } from 'src/configurations/configuration';
import { PreferenceKeys } from './interfaces/preference-keys.enum';

@Component({
  selector: 'vex-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  title = 'vex';

  // firebaseConfig$ = FirebaseConfig;

  constructor(
    private configService: ConfigService,
    private styleService: StyleService,
    private renderer: Renderer2,
    private platform: Platform,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string,
    private layoutService: LayoutService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private splashScreenService: SplashScreenService,
    public translate: TranslateService,
    private titleService: Title,
    private languageTranslationService: LanguageTranslationService
  ) {
    Settings.defaultLocale = this.localeId;

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }

    /**
     * Customize the template to your needs with the ConfigService
     * Example:
     *  this.configService.updateConfig({
     *    sidenav: {
     *      title: 'CusNom App',
     *      imageUrl: '//placehold.it/100x100',
     *      showCollapsePin: false
     *    },
     *    footer: {
     *      visible: false
     *    }
     *  });
     */

    this.configService.updateConfig({
      sidenav: {
        title: '',
        imageUrl: 'assets/img/logos/logo.png',
        showCollapsePin: true,
        state: 'expanded',
      },
      footer: {
        visible: false,
      },
    });

    /**
     * Config Related Subscriptions
     * You can remove this if you don't need the functionality of being able to enable specific configs with queryParams
     * Example: example.com/?layout=apollo&style=default
     */
    this.route.queryParamMap
      .pipe(
        map(
          queryParamMap =>
            queryParamMap.has('rtl') && coerceBooleanProperty(queryParamMap.get('rtl'))
        )
      )
      .subscribe(isRtl => {
        this.document.body.dir = isRtl ? 'rtl' : 'ltr';
        this.configService.updateConfig({
          rtl: isRtl,
        });
      });

    this.route.queryParamMap
      .pipe(filter(queryParamMap => queryParamMap.has('layout')))
      .subscribe(queryParamMap =>
        this.configService.setConfig(queryParamMap.get('layout') as ConfigName)
      );

    this.route.queryParamMap
      .pipe(filter(queryParamMap => queryParamMap.has('style')))
      .subscribe(queryParamMap =>
        this.styleService.setStyle(queryParamMap.get('style') as Style)
      );

    /**
     * Add your own routes here
     * @param userRole array of roles, ex [5, 7, 9] or send empty to allow all
     */
    this.navigationService.items = [
      {
        type: 'link',
        label: 'HOME',
        userRole: [5, 7, 8, 9],
        route: '/home',
        icon: icHome,
        routerLinkActiveOptions: { exact: true },
      },
      {
        type: 'dropdown',
        label: 'JOBS',
        userRole: [5, 7, 8, 9],
        icon: icJobs,
        children: [
          {
            type: 'link',
            label: 'JOB_DASHBOARD',
            userRole: [5, 7, 8, 9],
            route: '/apps/jobs',
          },
          {
            type: 'button',
            label: 'ADD_JOBS',
            userRole: [5, 7, 9],
            action: 'addJob',
          },
          // {
          //   type: 'button',
          //   label: 'Add Job Approval',
          //   userRole: [5, 7, 9],
          //   action: 'addJobApproval',
          // },
          {
            type: 'link',
            label: 'MY_JOBS',
            userRole: [5, 7, 8, 9],
            route: '/apps/jobs/jobs-listing',
          },
        ],
      },
      {
        type: 'dropdown',
        label: 'CANDIDATES',
        icon: icGroups,
        userRole: [5, 7, 8, 9],
        children: [
          // {
          //   type: 'link',
          //   label: 'Pending',
          //   userRole: [5, 7, 8, 9],
          //   route: '/apps/candidates/pending',
          // },
          {
            type: 'link',
            label: 'CANDIDATES',
            userRole: [5, 7, 8, 9],
            route: '/apps/candidates',
          },
          // {
          //   type: 'link',
          //   label: 'ONBOARDING',
          //   userRole: [8],
          //   route: '/apps/candidates/onboarding/card'
          // },
          // {
          //   type: 'link',
          //   label: 'EMPLOYEE',
          //   userRole: [5, 7, 8, 9],
          //   route: '/apps/candidates/employee/card'
          // }
        ],
      },
      {
        type: 'link',
        label: 'ADVANCED_SEARCH',
        userRole: [5, 7, 9],
        route: '/apps/search-advance',
        icon: icSearch,
      },
      {
        type: 'dropdown',
        label: 'SCHEDULE',
        icon: icDateRange,
        userRole: [5, 7, 8, 9],
        children: [
          {
            type: 'link',
            label: 'AVAILABILITY',
            userRole: [5, 7, 8, 9],
            route: '/apps/schedule/availability',
          },
          {
            type: 'link',
            label: 'SCHEDULE',
            userRole: [5, 7, 8, 9],
            route: '/apps/schedule/list',
          },
        ],
      },
      {
        type: 'dropdown',
        label: 'SETTINGS',
        icon: icSettings,
        userRole: [5, 7, 8, 9],
        children: [
          {
            type: 'link',
            label: 'COMPANY',
            userRole: [5],
            route: '/apps/settings/company',
          },
          {
            type: 'link',
            label: 'USERS',
            userRole: [5, 7, 8, 9],
            route: '/apps/settings/users',
          },
          {
            type: 'link',
            label: 'WORKFLOW',
            userRole: [5],
            route: '/apps/settings/work-flow',
          },
          {
            type: 'dropdown',
            label: 'RECRUITING_PREFERENCE',
            userRole: [5],
            children: [
              {
                type: 'link',
                label: 'LETTERS',
                userRole: [5],
                route: '/apps/settings/recruiting-preference/letter',
              },
              {
                type: 'link',
                label: 'CUSTOM_FIELDS',
                userRole: [5],
                route: '/apps/settings/recruiting-preference/custom-field/job',
              },
              {
                type: 'link',
                label: 'JOB_TEMPLATES',
                userRole: [5],
                route: '/apps/settings/recruiting-preference/job-template',
              },
              {
                type: 'link',
                label: 'QUESTIONS',
                userRole: [5],
                route: '/apps/settings/recruiting-preference/questions',
              },
              // {
              //   type: 'link',
              //   label: 'Interview Guides',
              //   userRole: [5],
              //   route: '/apps/settings/recruiting-preference/interview-guide',
              // },
              {
                type: 'link',
                label: 'SCORECARDS',
                userRole: [5],
                route: '/apps/settings/recruiting-preference/scorecard',
              },
            ],
          },
          {
            type: 'link',
            label: 'DEPARTMENTS',
            userRole: [5],
            route: '/apps/settings/departments',
          },
          // {
          //   type: 'link',
          //   label: 'ONBOARDING',
          //   userRole: [5],
          //   route: '/apps/settings/onboarding',
          // },
          {
            type: 'link',
            label: 'CAREER_PAGE',
            userRole: [5],
            route: '/apps/settings/career-page',
          },
          {
            type: 'link',
            label: 'INTEGRATIONS',
            userRole: [5],
            route: '/apps/settings/integrations',
          },
          // {
          //   type: 'link',
          //   label: 'AGENCY_SETUP',
          //   userRole: [5],
          //   route: '/apps/settings/agency-setup',
          // },
        ],
      },
      {
        type: 'dropdown',
        label: 'REPORTS',
        icon: icAssessment,
        userRole: [5, 7, 9],
        children: [
          {
            type: 'link',
            label: 'SAVED_REPORTS',
            userRole: [5, 7, 9],
            route: '/apps/reports/saved-report',
          },
          // {
          //   type: 'link',
          //   label: 'Candidate/Jobs Activity Reports',
          //   userRole: '',
          //   route: '/apps/reports/candidate-jobs-activity-report',
          // },
          // {
          //   type: 'link',
          //   label: 'Candidate Source Reports',
          //   userRole: '',
          //   route: '/apps/reports/candidate-source-report',
          // },
          {
            type: 'dropdown',
            label: 'BUILD_YOUR_OWN',
            userRole: [5, 7, 9],
            children: [
              {
                type: 'link',
                label: 'CANDIDATE',
                userRole: [5, 7, 9],
                route: '/apps/reports/candidate-report',
              },
              {
                type: 'link',
                label: 'JOB',
                userRole: [5, 7, 9],
                route: '/apps/reports/job-report',
              },
            ],
          },
          // {
          //   type: 'link',
          //   label: 'Candidate status',
          //   userRole: '',
          //   route: '/apps/settings/work-flow',
          // },
          // {
          //   type: 'dropdown',
          //   label: 'Job Status',
          //   userRole: '',
          //   children: [
          //     {
          //       type: 'link',
          //       label: 'Log Status',
          //       userRole: '',
          //       route: '/apps/settings/recruiting-preference/letter',
          //     },
          //   ],
          // },

          // {
          //   type: 'link',
          //   label: 'Time to Fill',
          //   userRole: '',
          //   route: '/apps/settings/departments',
          // },
          // {
          //   type: 'link',
          //   label: 'Questions',
          //   userRole: '',
          //   route: '/apps/settings/onboarding',
          // },
          {
            type: 'link',
            label: 'EEO',
            userRole: [5, 7, 9],
            route: '/apps/reports/eeo',
          },
          // {
          //   type: 'link',
          //   label: 'Onboarding',
          //   userRole: '',
          //   route: '/apps/settings/integration',
          // },
          // {
          //   type: 'link',
          //   label: 'Referral',
          //   userRole: '',
          //   route: '/apps/settings/agency-setup',
          // },
        ],
      },
      {
        type: 'link',
        label: 'AI_CHATBOT',
        route: '/apps/ai-chatbot',
        icon: icAssessment,
        userRole: [5, 7, 9],
      },
      // {
      //   type: 'link',
      //   label: 'Help',
      //   icon: icHelpOutline,
      //   route: '/documentation/further-help',
      //   userRole: [5, 7, 8, 9]
      // }
      {
        type: 'dropdown',
        label: 'HELP',
        userRole: [5, 7, 8, 9],
        icon: icHelpOutline,
        children: [
          {
            type: 'button',
            label: 'ACCOUNT_SETUP',
            userRole: [5, 7, 8, 9],
            action: 'accountSetup',
          },
          {
            type: 'button',
            label: 'SETUP_CAREER_PAGE',
            userRole: [5, 7, 8, 9],
            action: 'setupCareerPage',
          },
          {
            type: 'button',
            label: 'JOB_CREATION',
            userRole: [5, 7, 8, 9],
            action: 'jobCreation',
          },
          {
            type: 'button',
            label: 'MANAGE_CANDIDATE_PROFILE',
            userRole: [5, 7, 8, 9],
            action: 'manageCandidateProfile',
          },
          {
            type: 'button',
            label: 'EMAIL_TEMPLATE',
            userRole: [5, 7, 8, 9],
            action: 'emailTemplate',
          },
          {
            type: 'button',
            label: 'CUSTOM_FIELDS',
            userRole: [5, 7, 8, 9],
            action: 'customFields',
          },
          {
            type: 'button',
            label: 'PRESCREEN_QUESIONS',
            userRole: [5, 7, 8, 9],
            action: 'prescreenQuestions',
          },
          {
            type: 'button',
            label: 'JOB_TEMPLATE',
            userRole: [5, 7, 8, 9],
            action: 'jobTemplate',
          },
          // {
          //   type: 'link',
          //   label: 'HELP',
          //   userRole: [5, 7, 8, 9],
          //   route: '/documentation/further-help',
          // },
        ],
      },
    ];
  }

  ngOnInit() {
    // firebase.initializeApp(this.firebaseConfig$);

    this.languageTranslationService.selectedLanguage.subscribe(lang => {
      // this.translate.setDefaultLang(lang);
      this.translate.use(lang);

      const found = titleConfigs.find(config => config.key == lang);
      if (found) {
        this.titleService.setTitle(found.value);
      } else {
        const enTitle = titleConfigs.find(config => config.key == 'en');
        this.titleService.setTitle(enTitle.value);
      }
    });
  }
}
